import apiClient from './apiClient';

const CATALOG_FACETS_CACHE_KEY = 'catalogFacets';
const CATALOG_FACETS_CACHE_TS_KEY = 'catalogFacetsTimestamp';
const CATALOG_FACETS_TTL_MS = 5 * 60 * 1000;
let catalogFacetsMemoryCache = null;

// =====================================================
// CACHÉ COMPARTIDO + RETRY PARA /categories/
// =====================================================
// El navbar (created), el footer y el home piden las categorías por separado.
// Antes, cada uno hacía su propio GET sin reintento: si el primero fallaba
// (red lenta / cold start), el menú quedaba vacío hasta recargar la página.
// Ahora todos comparten la misma promesa y se reintenta con backoff.
const CATEGORIES_TTL_MS = 5 * 60 * 1000;
const CATEGORIES_RETRIES = 2;
let categoriesCache = null; // { timestamp, data, promise }

async function fetchCategoriesWithRetry(attempt = 0) {
  try {
    const response = await apiClient.get('/categories/', { timeout: 12000 });
    return response.data;
  } catch (error) {
    if (attempt < CATEGORIES_RETRIES) {
      // Backoff corto: 400ms, 900ms
      const delay = 400 * (attempt + 1) + attempt * 500;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return fetchCategoriesWithRetry(attempt + 1);
    }
    throw error;
  }
}

/**
 * Obtiene todas las categorías del servidor.
 * Comparte la promesa en curso entre llamadores y cachea el resultado 5 min.
 * @param {object} [options]
 * @param {boolean} [options.forceRefresh] - Si es true, ignora el caché
 * @returns {Promise<Array>} Lista de categorías
 */
function getCategories({ forceRefresh = false } = {}) {
  const now = Date.now();

  const isFresh =
    categoriesCache &&
    !forceRefresh &&
    now - categoriesCache.timestamp < CATEGORIES_TTL_MS;

  if (isFresh) {
    // Si todavía hay un fetch en curso, devolver esa misma promesa
    return categoriesCache.promise || categoriesCache.data;
  }

  if (categoriesCache && categoriesCache.promise) {
    return categoriesCache.promise;
  }

  const promise = fetchCategoriesWithRetry()
    .then((data) => {
      categoriesCache = { timestamp: Date.now(), data, promise: null };
      return data;
    })
    .catch((error) => {
      // No cachear el error: el próximo llamador vuelve a intentar
      categoriesCache = null;
      throw error;
    });

  categoriesCache = { timestamp: now, data: null, promise };
  return promise;
}

async function getCategoriesHierarchical() {
  try {
    const response = await apiClient.get('/categories/hierarchical/');
    console.log('Raw hierarchical response:', response.data);
    
    // Make sure we have the correct structure (sanity check)
    if (Array.isArray(response.data)) {
      // Ensure each category has at least an empty children array
      return response.data.map(category => ({
        ...category,
        children: category.children || [] 
      }));
    }
    
    return response.data;
  } catch (error) {
    console.error('Error al cargar categorías:', error);
    throw error;
  }
}

/**
 * Facetas del catálogo: todas las marcas y árbol padre → subcategorías → marcas con conteos.
 */
async function getCatalogFacets() {
  if (
    catalogFacetsMemoryCache &&
    Date.now() - catalogFacetsMemoryCache.timestamp < CATALOG_FACETS_TTL_MS
  ) {
    return catalogFacetsMemoryCache.data;
  }

  try {
    const cached = localStorage.getItem(CATALOG_FACETS_CACHE_KEY);
    const timestamp = Number(localStorage.getItem(CATALOG_FACETS_CACHE_TS_KEY));

    if (cached && timestamp && Date.now() - timestamp < CATALOG_FACETS_TTL_MS) {
      const data = JSON.parse(cached);
      catalogFacetsMemoryCache = { data, timestamp };
      return data;
    }
  } catch (error) {
    localStorage.removeItem(CATALOG_FACETS_CACHE_KEY);
    localStorage.removeItem(CATALOG_FACETS_CACHE_TS_KEY);
  }

  const response = await apiClient.get('/products/filter-facets/', {
    timeout: 12000,
  });
  const data = response.data || { all_brands: [], parents: [] };
  const timestamp = Date.now();

  catalogFacetsMemoryCache = { data, timestamp };
  try {
    localStorage.setItem(CATALOG_FACETS_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(CATALOG_FACETS_CACHE_TS_KEY, String(timestamp));
  } catch (error) {
    // Ignore storage quota/private-mode errors; in-memory cache still works.
  }

  return data;
}

export {
  getCategories,
  getCategoriesHierarchical,
  getCatalogFacets
};
